import React, { useState } from 'react';
import {
  Box, Typography, IconButton, Paper, Collapse, Stack, Chip, TextField, MenuItem, Button, CircularProgress,
} from '@mui/material';
import { Ask, Hint } from '../../../components/wizardKit/basics';
import { Field, LabeledInput, Seg } from '../../../components/wizardKit/controls';
import { C } from '../../../components/wizardKit/tokens';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useDataProvider, useNotify } from 'react-admin';
// window.confirm() não passa pelo plugin de build que embrulha texto de JSX/notify — precisa
// do __t à mão pra funcionar em espanhol (ver src/i18n/index.ts). Caminho relativo, não o
// alias '@i18n/t': ele só existe no vite.config.ts (usado pelo plugin gerado no build), e o
// tsconfig.json não tem esse path mapeado para o tsc.
import { __t } from '../../../i18n/t';
import LevelRewardEditor, { emptyReward, LevelRewardValue, REWARD_TYPE_LABEL, RewardValue } from './LevelRewardEditor';

export interface RecoveryDiscount {
  id: string;
  identify: string;
  description: string | null;
}

export interface LevelValue {
  levelOrder: number;
  /** Nível de desconto. Exclusivo com `reward`. */
  discountId: string | null;
  /** Nível de brinde. Exclusivo com `discountId`. */
  reward: LevelRewardValue | null;
  durationDays: number;
  notificationTitle: string | null;
  notificationBody: string | null;
}

/** Só o que o endpoint de sugestão precisa do público — mesmo formato de AudienceStep. */
export interface SuggestAudience {
  minDaysWithoutFueling: number;
  maxDaysWithoutFueling: number | null;
  segment: 'at_risk' | 'inactive' | 'custom';
}

interface LevelsStepProps {
  chainId: string;
  levels: LevelValue[];
  onChange: (levels: LevelValue[]) => void;
  recoveryDiscounts: RecoveryDiscount[];
  audience: SuggestAudience;
  sendPush: boolean;
  sendSms: boolean;
  /** Custo estimado da campanha no cenário escolhido, e o teto — alimentam a trava de sanidade. */
  estimatedCost?: number | null;
  maxTotalSpend?: number | null;
}

export const MAX_LEVELS = 10;

const money = (value: number) => `R$ ${value.toFixed(2).replace('.', ',')}`;

// Cores do tipo "Recuperação" (as mesmas da lista de descontos). O texto é o tom escuro:
// o laranja puro sobre branco não chega a 4,5:1 em corpo de 12 px.
const LEVEL_CHIP_SX = {
  bgcolor: 'rgba(230,81,0,.09)',
  color: '#b43209',
  border: '1px solid rgba(230,81,0,.22)',
  fontWeight: 700,
} as const;

const WARN_CHIP_SX = { bgcolor: '#fff4e5', color: '#663c00', border: '1px solid rgba(237,108,2,.30)' } as const;

/** Nível sendo editado: a borda violeta é o que diz qual cartão está aberto. */
const OPEN_CARD_SX = { borderColor: 'rgba(120,105,223,.32)', boxShadow: '0 4px 16px rgba(30,41,59,.06)' } as const;

const SECTION_TITLE_SX = { fontSize: 14.5, fontWeight: 700, color: C.ink } as const;

const HEADER_OPEN_SX = { borderBottom: `1px solid ${C.lineSoft}` } as const;

// Valor de alinhamento usado dentro de ternário no JSX: em literal solto, o plugin de i18n
// embrulha a string em __t() e ela vira "texto a traduzir" (mesma regra do discounts/v2).
const ALIGN_END = 'flex-end';
const ALIGN_CENTER = 'center';

// Rótulo acima de um controle que não é campo de texto (o alternador), nas métricas do
// LabeledInput: é o que mantém a linha alinhada com os campos ao lado.
// `alignItems` é o que segura a largura do alternador: numa coluna, o padrão `stretch`
// o estica pela largura do cartão inteiro, mesmo com width 'auto'.
const SEG_COLUMN_SX = { display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' } as const;
const SEG_LABEL_SX = { fontSize: 12.5, fontWeight: 500, color: C.muted } as const;
const FIELD_COLUMN_SX = { display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 } as const;

// A largura de cada campo é o `width` do LabeledInput (máximo em px, 100% da linha quando
// ela é mais estreita): a tabela de breakpoints daqui virou isso.

const LevelsStep: React.FC<LevelsStepProps> = ({
  chainId, levels, onChange, recoveryDiscounts, audience, sendPush, sendSms, estimatedCost, maxTotalSpend,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const needsMessage = sendPush || sendSms;
  const [generatingIndex, setGeneratingIndex] = useState<number | null>(null);
  // Um nível aberto por vez: com dez abertos, a etapa vira um rolo em que ninguém acha o
  // campo que falta. O fechado mostra em uma linha o que já foi escolhido.
  const [openIndex, setOpenIndex] = useState(0);

  const addLevel = () => {
    if (levels.length >= MAX_LEVELS) return;
    const order = levels.length + 1;
    onChange([...levels, {
      levelOrder: order, discountId: null, reward: null, durationDays: 5,
      notificationTitle: null, notificationBody: null,
    }]);
    setOpenIndex(levels.length);
  };

  const removeLevel = (index: number) => {
    if (levels.length <= 1) return;
    onChange(levels.filter((_, i) => i !== index).map((l, i) => ({ ...l, levelOrder: i + 1 })));
    // Remover renumera: sem isto, o aberto passaria a ser o nível de baixo.
    setOpenIndex((open) => (open >= index && open > 0 ? open - 1 : open));
  };

  const overBudget = estimatedCost != null && maxTotalSpend != null && maxTotalSpend > 0
    && estimatedCost > maxTotalSpend;

  const updateLevel = (index: number, patch: Partial<LevelValue>) => {
    onChange(levels.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  const discountUsedElsewhere = (discountId: string, index: number) =>
    levels.some((l, i) => i !== index && l.discountId === discountId);

  /** Nível pronto o bastante para simular ou gerar mensagem: tem desconto ou um brinde com recompensa. */
  const levelHasReward = (level: LevelValue) =>
    !!level.discountId || (!!level.reward && level.reward.rewards.length > 0);

  /** A linha que o nível fechado mostra no lugar do formulário. */
  const levelSummary = (level: LevelValue) => {
    const dias = __t('%{dias} dias', { dias: level.durationDays });
    if (level.reward) {
      const tipos = [...new Set(level.reward.rewards.map((r) => __t(REWARD_TYPE_LABEL[r.type])))].join(' + ');
      return `${__t('Brinde')} · ${tipos || __t('sem recompensa')} · ${dias}`;
    }
    const discount = recoveryDiscounts.find((d) => d.id === level.discountId);
    return `${__t('Desconto no combustível')} · ${discount?.identify || __t('nenhum escolhido')} · ${dias}`;
  };

  const setKind = (index: number, kind: 'discount' | 'reward') =>
    updateLevel(index, kind === 'discount'
      ? { reward: null, discountId: recoveryDiscounts.find((d) => !discountUsedElsewhere(d.id, index))?.id ?? null }
      : { discountId: null, reward: emptyReward() });

  /**
   * "Gerar com IA" (POST /customer-recovery-rules/suggest-message). Nunca grava nada — só
   * preenche título/mensagem do nível, e o funcionário continua podendo editar depois.
   * Só manda pro backend os níveis que já têm desconto escolhido (a API exige discountId
   * em cada item), o que é o suficiente pra ela achar o nível pedido e o próximo.
   */
  const handleGenerate = async (index: number) => {
    const level = levels[index];
    if (!levelHasReward(level) || generatingIndex !== null) return;

    if (level.notificationTitle || level.notificationBody) {
      const confirmed = window.confirm(__t('Isso substitui o título e a mensagem já preenchidos deste nível. Continuar?'));
      if (!confirmed) return;
    }

    setGeneratingIndex(index);
    try {
      const { data } = await dataProvider.create('customer-recovery-rules/suggest-message', {
        data: {
          chainId,
          levelOrder: level.levelOrder,
          // Só os níveis já preenchidos — é o suficiente pra API achar o pedido e o próximo.
          // Do brinde vai só o tipo da recompensa e o requisito: o título é texto livre do
          // operador e o custo é número interno do posto, nenhum dos dois entra no prompt.
          levels: levels.filter(levelHasReward).map((l) => ({
            levelOrder: l.levelOrder,
            durationDays: l.durationDays,
            discountId: l.discountId,
            reward: l.reward
              ? {
                rewardTypes: [...new Set(l.reward.rewards.map((r: RewardValue) => r.type))],
                requirement: {
                  type: l.reward.requirement.type,
                  value: l.reward.requirement.value,
                  valueType: l.reward.requirement.valueType,
                },
              }
              : null,
          })),
          audience,
          sendPush,
          sendSms,
        },
      });
      updateLevel(index, { notificationTitle: data.title, notificationBody: data.body });
      notify('Sugestão gerada — revise antes de salvar.', { type: 'success' });
    } catch (error: any) {
      notify(error?.message || 'Não foi possível gerar a sugestão agora.', { type: 'warning' });
    } finally {
      setGeneratingIndex(null);
    }
  };

  return (
    <Box sx={{ pt: 2.25, pb: 3.5, maxWidth: 968, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
      <Ask
        id="q-niveis"
        title="Quais níveis a campanha vai percorrer?"
        help={__t('Todos os clientes avançam juntos. Cada nível dura os dias definidos e depois avança (ou encerra a campanha, no último nível).')}
      />

      <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Hint>{levels.length}/{MAX_LEVELS} níveis</Hint>
        <Button size="small" startIcon={<AddIcon />} onClick={addLevel} disabled={levels.length >= MAX_LEVELS}>
          Adicionar nível
        </Button>
      </Box>

      {recoveryDiscounts.length === 0 && levels.some((l) => !l.reward) && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'warning.main', mb: 2 }}>
          <LocalGasStationIcon fontSize="small" />
          <Typography variant="body2">
            Nenhum desconto do tipo Recuperação disponível nesta rede. Cadastre um em Descontos, ou troque o nível para brinde.
          </Typography>
        </Stack>
      )}

      {/* Trava de sanidade: o custo do brinde é fixo por cliente, então um pacote caro estoura
          o teto com pouquíssimas voltas. Aqui o operador vê isso antes de salvar, não depois. */}
      {overBudget && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ color: 'error.main', mb: 2 }}>
          <WarningAmberIcon fontSize="small" />
          <Typography variant="body2">
            {__t('O custo estimado (%{cost}) passa do teto de gasto (%{budget}). A campanha encerraria sozinha no meio do percurso — baixe o custo das recompensas ou suba o teto.',
              { cost: money(estimatedCost!), budget: money(maxTotalSpend!) })}
          </Typography>
        </Stack>
      )}

      {levels.map((level, index) => {
        const choices = recoveryDiscounts.filter((d) => d.id === level.discountId || !discountUsedElsewhere(d.id, index));
        const generating = generatingIndex === index;
        const kind: 'discount' | 'reward' = level.reward ? 'reward' : 'discount';
        const aberto = openIndex === index;

        return (
          <Paper key={index} variant="outlined" sx={{ mb: 1.5, ...(aberto ? OPEN_CARD_SX : null) }}>
            <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: aberto ? ALIGN_END : ALIGN_CENTER, ...(aberto ? HEADER_OPEN_SX : null) }}>
              <Chip label={`Nível ${level.levelOrder}`} size="small" sx={LEVEL_CHIP_SX} />

              {aberto ? (
                <>
                  <LabeledInput
                    id={`nivel-${level.levelOrder}-dias`}
                    label="Duração"
                    // `suffix` não está em WRAP_ATTRS: aqui o __t é à mão (como no desconto v2).
                    suffix={__t('dias')}
                    inputMode="numeric"
                    width={150}
                    value={String(level.durationDays)}
                    onChange={(e) => updateLevel(index, { durationDays: +e.target.value || 1 })}
                  />
                </>
              ) : (
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary">{levelSummary(level)}</Typography>
                </Box>
              )}

              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* O nível fechado precisa dizer sozinho que ainda falta coisa: senão o aviso
                    de validação aponta para um cartão que não mostra onde está o buraco. */}
                {!aberto && !levelHasReward(level) && (
                  <Chip size="small" icon={<WarningAmberIcon />} label="Falta completar" sx={WARN_CHIP_SX} />
                )}
                {!aberto && (
                  <Button size="small" onClick={() => setOpenIndex(index)}>Editar</Button>
                )}
                {levels.length > 1 && (
                  <IconButton
                    onClick={() => removeLevel(index)}
                    color="error"
                    size="small"
                    // Remover renumera os níveis: com a IA gerando, a resposta cairia no nível errado.
                    disabled={generatingIndex !== null}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            </Box>

            <Collapse in={aberto}>
              <Stack spacing={2.5} sx={{ p: 2 }}>
                <Box sx={SEG_COLUMN_SX}>
                  <Box component="span" sx={SEG_LABEL_SX}>O que o nível dá</Box>
                  <Seg
                    value={kind}
                    onChange={(value) => setKind(index, value)}
                    tone="brand"
                    label={__t('O que o nível %{n} dá', { n: level.levelOrder })}
                    options={[
                      { value: 'discount', label: 'Desconto no combustível' },
                      { value: 'reward', label: 'Brinde' },
                    ]}
                  />
                </Box>

                {kind === 'discount' && (
                  <Field
                    id={`nivel-${level.levelOrder}-desconto`}
                    label="Desconto de recuperação"
                    width={420}
                    hint={recoveryDiscounts.length === 0
                      ? __t('Cadastre um desconto tipo Recuperação em /discounts.')
                      : __t('Cada nível precisa de um desconto diferente: os já usados não aparecem aqui.')}
                  >
                    <TextField
                      select
                      id={`nivel-${level.levelOrder}-desconto`}
                      fullWidth
                      value={level.discountId || ''}
                      onChange={(e) => updateLevel(index, { discountId: e.target.value || null })}
                    >
                      {choices.map((d) => (
                        <MenuItem key={d.id} value={d.id}>
                          {d.description ? `${d.identify} — ${d.description}` : d.identify}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Field>
                )}

                {level.reward && (
                  <LevelRewardEditor
                    chainId={chainId}
                    value={level.reward}
                    onChange={(patch) => updateLevel(index, { reward: { ...level.reward!, ...patch } })}
                  />
                )}

                <Box sx={{ borderTop: `1px solid ${C.lineSoft}`, pt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={SECTION_TITLE_SX}>Mensagem da notificação</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ ml: 'auto' }}
                      startIcon={generating ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />}
                      disabled={!levelHasReward(level) || generating}
                      onClick={() => handleGenerate(index)}
                    >
                      {generating ? __t('Gerando…') : __t('Gerar com IA')}
                    </Button>
                  </Box>
                  <LabeledInput
                    id={`nivel-${level.levelOrder}-push-titulo`}
                    label="Título da mensagem"
                    value={level.notificationTitle || ''}
                    onChange={(e) => updateLevel(index, { notificationTitle: e.target.value || null })}
                    width={480}
                  />
                  <Box sx={{ ...FIELD_COLUMN_SX, maxWidth: 640 }}>
                    <LabeledInput
                      id={`nivel-${level.levelOrder}-push-corpo`}
                      label="Corpo da mensagem"
                      multiline
                      minRows={2}
                      width={640}
                      value={level.notificationBody || ''}
                      onChange={(e) => updateLevel(index, { notificationBody: e.target.value || null })}
                      describedBy={`nivel-${level.levelOrder}-push-ajuda`}
                    />
                    <Hint id={`nivel-${level.levelOrder}-push-ajuda`}>
                      {!sendSms
                        ? __t('Use {{nome_do_cliente}} para o primeiro nome (só funciona no push).')
                        : __t('Com SMS ligado, {{nome_do_cliente}} não pode ser usado — o SMS manda o texto cru.')}
                    </Hint>
                  </Box>
                </Box>
              </Stack>
            </Collapse>
          </Paper>
        );
      })}
      </Box>
    </Box>
  );
};

export default LevelsStep;
